import { useReactiveVar } from '@apollo/client'
import { dictionariesVar, projectVar } from '../../../apollo/reactive-vars'
import { CostType } from '../../../generated/graphql'
import { getProviderIdsToMultipliers } from '../../../project-versions/version-util'
import { GroupedFixedCost } from './cost-table-types'
import { groupCosts } from './cost-table-util'

type UseCostTables = {
  groupProjectCostsByCostType: () => Record<CostType, GroupedFixedCost[]>
  checkIfProviderExistsForVersion: (
    containerName: string,
    providerName: string,
    versionId: string,
  ) => boolean
}

export function useCostTables(): UseCostTables {
  const {
    providerIdsToNames,
    providerIdsToContainerNames,
    versionIdsToNames,
    costIdsToVersionIds,
    getSystemContainerTitle,
  } = useReactiveVar(dictionariesVar)
  const project = useReactiveVar(projectVar)
  const versions = project?.versions ?? []

  const groupProjectCostsByCostType = (): Record<
    CostType,
    GroupedFixedCost[]
  > => {
    const costs = versions.flatMap((version) => version.costs)
    const multipliers = getProviderIdsToMultipliers(versions)

    return Object.keys(CostType).reduce((records, costType) => {
      const costsByType = costs.filter((cost) => cost.costType === costType)
      const groupedCosts = groupCosts(
        providerIdsToNames,
        providerIdsToContainerNames,
        costIdsToVersionIds,
        versionIdsToNames,
        costsByType,
        multipliers,
      )
      return { ...records, [costType]: groupedCosts }
    }, {} as Record<CostType, GroupedFixedCost[]>)
  }

  const checkIfProviderExistsForVersion = (
    containerName: string,
    providerName: string,
    versionId: string,
  ): boolean => {
    if (containerName === '') {
      return true
    }
    const version = versions.find(
      (projectVersion) => projectVersion.id === versionId,
    )
    if (!version) {
      throw new Error('Project version not found in provider check')
    }
    const matchingProvider = [...version.buildings, ...version.localGrids]
      .find(
        (systemContainer) =>
          getSystemContainerTitle(systemContainer) === containerName,
      )
      ?.providers.find((provider) => provider.name === providerName)
    if (!matchingProvider) {
      return false
    }
    return true
  }

  return {
    groupProjectCostsByCostType,
    checkIfProviderExistsForVersion,
  }
}
